import { useApp } from 'contexts/app';
import { useDialog } from 'contexts/dialog';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Paper, RichTextArea, Stack, Text } from 'shell/components';
import { getGradingSettingsErrors, isGradingInstructionListOverLimit } from 'utils/assignmentSettingsValidation';
import { CUSTOM_CONTENT_WORD_COUNT_LIMIT } from 'utils/constants';
import { getHTMLWordCount } from 'utils/helper';
import AssignmentSettingsPanel from './AssignmentSettingsPanel';
import { AssignmentSettingToggleCard } from './AssignmentSettingsPanelControls';
import CriterionInstructions from './CriterionInstructions';

const AssignmentGradingPanel = ({
    form,
    setForm,
    errors,
    setErrors,
    isEditable,
    onPendingDraftChange,
    draftResetKey,
}) => {
    const { isGradingFeatureDisabled } = useApp();
    const { setDialog, closeDialog } = useDialog();
    const { t } = useTranslation('assignment');
    const { t: settingsT } = useTranslation('assignmentSettings');
    const [pendingDrafts, setPendingDrafts] = useState({});
    const [listErrors, setListErrors] = useState({});
    const enabled = form.aiGradingEnabled && !isGradingFeatureDisabled;
    const disabled = !isEditable || !enabled;
    const detailErrors = getGradingSettingsErrors(form);
    const showValidationErrors = Boolean(errors?.gradingSettings);
    const visibleDetailErrors = showValidationErrors ? detailErrors : {};
    const hasPendingDrafts = (form.rubric?.criteria || []).some(({ id }) => pendingDrafts[id]);

    useEffect(() => {
        setPendingDrafts({});
        setListErrors({});
    }, [draftResetKey]);
    useEffect(() => {
        const criterionIds = new Set((form.rubric?.criteria || []).map(({ id }) => String(id)));
        setPendingDrafts((current) => {
            const entries = Object.entries(current).filter(([id]) => criterionIds.has(id));
            return entries.length === Object.keys(current).length ? current : Object.fromEntries(entries);
        });
    }, [form.rubric?.criteria]);
    useEffect(() => {
        onPendingDraftChange?.(hasPendingDrafts);
    }, [hasPendingDrafts, onPendingDraftChange]);
    useEffect(() => () => onPendingDraftChange?.(false), [onPendingDraftChange]);

    const updateDraft = useCallback((id, value) => {
        setPendingDrafts((current) => ({ ...current, [id]: value }));
        setListErrors((current) => ({ ...current, [id]: {} }));
    }, []);

    const changeEnabled = (value) => {
        const apply = () =>
            setForm((current) => ({
                ...current,
                aiGradingEnabled: value,
                ...(!value &&
                    current.feedbackSettings?.structure === 'templated' && {
                        feedbackSettings: { ...current.feedbackSettings, structure: 'system' },
                    }),
                ...(!value &&
                    current.studentFeedbackSettings?.structure === 'templated' && {
                        studentFeedbackSettings: { ...current.studentFeedbackSettings, structure: 'system' },
                    }),
            }));
        if (
            !value &&
            (form.feedbackSettings?.structure === 'templated' ||
                form.studentFeedbackSettings?.structure === 'templated')
        ) {
            setDialog({
                open: true,
                message: t(
                    'dialog.disable-grading-reset-warning.title',
                    'Disabling Grading will Reset Feedback to Default Settings'
                ),
                description: t(
                    'dialog.disable-grading-reset-warning.description',
                    'Smart Feedback Templates are currently selected, which requires grading to be enabled. If you disable grading, feedback will be changed to the system default. Do you want to continue?'
                ),
                note: '',
                variant: 'destructive',
                actionButtonText: t('common:controls.continue', 'Continue'),
                cancelButtonText: t('common:controls.cancel', 'Cancel'),
                onAction: () => {
                    closeDialog();
                    apply();
                },
                onCancel: closeDialog,
                onClose: closeDialog,
            });
        } else apply();
    };

    return (
        <AssignmentSettingsPanel
            enabled={enabled}
            toggle={
                <AssignmentSettingToggleCard
                    title={t('grading-section.grading-suggestions', 'Grading Suggestions')}
                    description={t(
                        'grading-section.assignment-description',
                        'TimelyGrader leverages assignment details to provide instructors with personalized grading suggestions for student’s final submissions. If disabled, grading must be done manually.'
                    )}
                    checked={enabled}
                    disabled={!isEditable}
                    restricted={isGradingFeatureDisabled}
                    onChange={changeEnabled}
                />
            }
        >
            <Stack spacing={2}>
                <Text component={'h2'} variant={'p1'} weight={'medium'}>
                    {settingsT('panels.grading.instructionsTitle', 'Adding Grading Instructions for the AI')}
                </Text>
                <Text color={'light'}>
                    {t(
                        'advanced-grading.description',
                        'In this section, you can give the AI extra guidance for its grading suggestions. These instructions can be general or tailored to each specific criterion.'
                    )}
                </Text>
            </Stack>
            <RichTextArea
                disabled={disabled}
                error={enabled && visibleDetailErrors.gradingCustomContext}
                aria-label={t('advanced-grading.general-instructions', 'General Instructions')}
                placeholder={t(
                    'advanced-grading.grading-suggestions-placeholder',
                    'Do you have any additional instructions about grading suggestions?'
                )}
                value={form.gradingCustomContext || ''}
                helperText={t('remaining-word-count', '{{wordLimitStatus}} words', {
                    wordLimitStatus: `${getHTMLWordCount(form.gradingCustomContext || '')}/${CUSTOM_CONTENT_WORD_COUNT_LIMIT}`,
                })}
                helperTextPosition={'end'}
                onChange={(value) => {
                    if (!disabled) setForm((current) => ({ ...current, gradingCustomContext: value }));
                }}
            />
            <Stack spacing={2}>
                <Text component={'h2'} variant={'p1'} weight={'medium'}>
                    {t('advanced-grading.criterion-instructions', 'Criterion-Specific Instructions')}
                </Text>
                <Text color={'light'}>
                    {t(
                        'advanced-grading.criterion-instructions-description',
                        'For each criterion, tell the AI what it would see or hear if the work were done well. Write these the way you would brief a teaching assistant on what to watch for.'
                    )}
                </Text>
            </Stack>
            {!form.rubric?.criteria?.length ? (
                <Paper sx={{ position: 'relative', minHeight: '160px', width: '100%' }} variant={'outlined'}>
                    <Text
                        align={'center'}
                        color={'light'}
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            width: '100%',
                            transform: 'translate(-50%, -50%)',
                        }}
                    >
                        {t(
                            'advanced-grading.criterion-instruction-missing-rubric',
                            'Please create a rubric in “General Assignment Details” to enable criterion-specific grading instructions.'
                        )}
                    </Text>
                </Paper>
            ) : (
                (form.rubric.criteria || []).map((criterion, index) => (
                    <CriterionInstructions
                        key={`${draftResetKey}:${criterion.id}`}
                        criterion={criterion}
                        defaultExpanded={index === 0}
                        disabled={disabled}
                        instruction={form.gradingSettings?.criteria?.[criterion.id]}
                        error={{
                            ...(visibleDetailErrors.criteria?.[criterion.id] || {}),
                            ...listErrors[criterion.id],
                        }}
                        onDraftChange={(value) => updateDraft(criterion.id, value)}
                        onListAdd={(listKey, next) => {
                            const overLimit = isGradingInstructionListOverLimit(next[listKey]);
                            setListErrors((current) => ({
                                ...current,
                                [criterion.id]: { ...current[criterion.id], [listKey]: overLimit },
                            }));
                            return !overLimit;
                        }}
                        onChange={(next) => {
                            if (disabled) return;
                            setForm((current) => ({
                                ...current,
                                gradingSettings: {
                                    ...current.gradingSettings,
                                    criteria: { ...current.gradingSettings?.criteria, [criterion.id]: next },
                                },
                            }));
                        }}
                    />
                ))
            )}
        </AssignmentSettingsPanel>
    );
};
export default AssignmentGradingPanel;
